export interface MemoryStore {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
  keys(): Promise<string[]>;
  clearNamespace(): Promise<void>;
}

type MaybePromise<T> = T | Promise<T>;

function createInMemoryStore(prefix: string): MemoryStore {
  const map = new Map<string, string>();

  const makeKey = (key: string) => `${prefix}-${key}`;

  return {
    async getItem(key: string): Promise<string | null> {
      return map.get(makeKey(key)) ?? null;
    },
    async setItem(key: string, value: string): Promise<void> {
      map.set(makeKey(key), value);
    },
    async removeItem(key: string): Promise<void> {
      map.delete(makeKey(key));
    },
    async keys(): Promise<string[]> {
      const result: string[] = [];
      for (const k of map.keys()) {
        if (k.startsWith(`${prefix}-`)) result.push(k);
      }
      return result;
    },
    async clearNamespace(): Promise<void> {
      for (const k of map.keys()) {
        if (k.startsWith(`${prefix}-`)) map.delete(k);
      }
    },
  };
}

function chainStores(primary: MemoryStore, fallback: MemoryStore): MemoryStore {
  return {
    async getItem(key: string): Promise<string | null> {
      try {
        return await primary.getItem(key);
      } catch {
        return await fallback.getItem(key);
      }
    },
    async setItem(key: string, value: string): Promise<void> {
      try {
        await primary.setItem(key, value);
      } catch {
        await fallback.setItem(key, value);
      }
    },
    async removeItem(key: string): Promise<void> {
      try {
        await primary.removeItem(key);
      } catch {
        await fallback.removeItem(key);
      }
    },
    async keys(): Promise<string[]> {
      try {
        return await primary.keys();
      } catch {
        return await fallback.keys();
      }
    },
    async clearNamespace(): Promise<void> {
      try {
        await primary.clearNamespace();
      } catch {
        await fallback.clearNamespace();
      }
    },
  };
}

function hasIndexedDB(): boolean {
  try {
    return typeof indexedDB !== 'undefined';
  } catch {
    return false;
  }
}

export async function createMemoryStore(
  prefix: string,
  webStorage?: Storage,
): Promise<MemoryStore> {
  // Dynamically import to avoid bundling cost if not needed
  const inMemory = createInMemoryStore(prefix);

  if (hasIndexedDB()) {
    try {
      const { createIndexedDbStore } = await import('./memory-store-indexeddb');
      const idbStore = createIndexedDbStore(prefix);
      if (webStorage) {
        const { createWebStorageStore } = await import('./memory-store-webstorage');
        const wsStore = createWebStorageStore(webStorage, prefix);
        return chainStores(idbStore, chainStores(wsStore, inMemory));
      }
      return chainStores(idbStore, inMemory);
    } catch {
      // Fall through to WebStorage / in-memory
    }
  }

  if (webStorage) {
    try {
      const { createWebStorageStore } = await import('./memory-store-webstorage');
      const wsStore = createWebStorageStore(webStorage, prefix);
      return chainStores(wsStore, inMemory);
    } catch {
      // Fall through
    }
  }

  return inMemory;
}


